import { Alert, Box, Icon, IconButton, Typography } from "@outschool/backpack";
import {
  CreditTransactionType,
  EsaCreditTransactionsQuery,
  EsaCreditTransactionsQueryVariables,
  TransactionType,
} from "@outschool/gql-frontend-generated";
import { faArrowUpRightFromSquare, faTimes } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useSessionStorageState } from "@outschool/session-storage";
import { dayjs } from "@outschool/time";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { ExternalLink } from "@outschool/ui-components-shared";
import Cookies from "js-cookie";
import React, { useState } from "react";

import * as Routes from "../../../shared/Routes";
import {
  formatEsaWalletCents,
  useEsaMembershipCreditBalances,
} from "../../hooks/useEsaMembershipCreditBalance";
import { useUpdateCurrentUserMutation } from "../../queries/CurrentUser";
import { CreditTransactionListing } from "../transactions/utils";

const esaTransactionsQuery = gql`
  query EsaCreditTransactions($limit: Int!, $offset: Int!) {
    transactionListings(limit: $limit, offset: $offset) {
      totalCount
      data {
        id
        date
        type
        ... on CreditTransactionListing {
          creditTransaction {
            transaction_type
          }
          creditPurchase {
            amount_cents
            amount_granted_credits
            learner {
              name
            }
          }
        }
        ... on ClassWalletTransactionListing {
          creditPurchase {
            amount_cents
            amount_granted_credits
            learner {
              name
            }
          }
        }
      }
    }
  }
`;

const SAW_ESA_MEMBERSHIP_BANNER_KEY = "sawEsaMembershipBanner";
const SESSION_MEMBERSHIP_BANNER_KEY = "showEsaMembershipBanner";

export default function EsaMembershipBanner({
  bannerClosedAt,
}: {
  bannerClosedAt: string | null | undefined;
}) {
  const { t } = useTranslation("client\\components\\banners\\EsaBanner");
  const [updateCurrentUser] = useUpdateCurrentUserMutation();
  const [showSessionBanner, setShowSessionBanner] =
    useSessionStorageState<boolean>(SESSION_MEMBERSHIP_BANNER_KEY, false);
  const { creditBalances, loading: balancesLoading } =
    useEsaMembershipCreditBalances();
  const [isClosed, setIsClosed] = useState(false);
  const { loading, data } = useQueryWithPreviousData<
    EsaCreditTransactionsQuery,
    EsaCreditTransactionsQueryVariables
  >(esaTransactionsQuery, {
    fetchPolicy: "cache-and-network",
    variables: {
      limit: 10,
      offset: 0,
    },
  });
  const currentDate = React.useMemo(() => dayjs(), []);

  if (isClosed || loading || balancesLoading || !data) {
    return null;
  }

  const transactionListings = data?.transactionListings
    ?.data as CreditTransactionListing[];
  if (!transactionListings || transactionListings.length === 0) {
    return null;
  }

  /**
   * Hack to only grab the esa credit deposits. At some point I think
   * this needs to be refactored to only query for a specific type of
   * transaction
   */
  const validTransactions = transactionListings.filter(
    t =>
      t.creditTransaction?.transaction_type ===
        CreditTransactionType.EsaMembershipDeposit ||
      (t.type === TransactionType.ClassWallet && t.creditPurchase),
  );
  if (validTransactions.length === 0) {
    return null;
  }

  // displaying most recent
  const { creditPurchase, date, type: transactionType } = validTransactions[0];
  if (!creditPurchase) {
    return null;
  }

  const isPendingTransaction = transactionType === TransactionType.ClassWallet;
  const showPendingBanner =
    isPendingTransaction &&
    currentDate.subtract(2, "week").isSameOrBefore(date, "day");

  const learnerName = creditPurchase?.learner?.name;
  const programName = creditBalances.filter(
    balance => balance.name === learnerName,
  )?.[0]?.programName;

  if (bannerClosedAt && date < bannerClosedAt) {
    setIsClosed(true);
  }

  const handleClose = async () => {
    setIsClosed(true);
    await updateCurrentUser({
      variables: { details: { ["bannerClosedAt"]: new Date().toISOString() } },
    });
  };

  const sawEsaMembershipBanner = Cookies.get(SAW_ESA_MEMBERSHIP_BANNER_KEY);

  if (!sawEsaMembershipBanner || date > sawEsaMembershipBanner) {
    Cookies.set(SAW_ESA_MEMBERSHIP_BANNER_KEY, new Date().toISOString(), {
      expires: 30,
    });
    setShowSessionBanner(true);
  } else if (date < sawEsaMembershipBanner && !showSessionBanner) {
    return null;
  }

  const successCloseButtonSx = {
    color: "success.dark",
    "&:enabled": {
      color: "success.dark",
    },
    "&:hover:not(:disabled), &:focus, &:active": {
      color: "success.dark",
      backgroundColor: "success.100",
    },
  };

  const pendingCloseButtonSx = {
    color: "info.dark",
    "&:enabled": {
      color: "info.dark",
    },
    "&:hover:not(:disabled), &:focus, &:active": {
      color: "info.dark",
      backgroundColor: "info.100",
    },
  };

  return (
    <Alert
      severity={showPendingBanner ? "info" : "success"}
      noAction
      icon={false}
      sx={{
        display: "flex",
        paddingY: "6px",
        justifyContent: "center",
        alignItems: "center",
        border: "none",
        borderRadius: "0",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <Typography variant="subtitle1" sx={{ color: "success" }}>
          {showPendingBanner ? (
            <>
              {t(
                "{{learnerName}} may have a pending {{programName}} fund purchase. Check status in",
                {
                  learnerName,
                  programName,
                },
              )}{" "}
              <ExternalLink url={Routes.classWalletOrderManagementUrl()}>
                {t("ClassWallet")} <Icon icon={faArrowUpRightFromSquare} />
              </ExternalLink>
            </>
          ) : (
            t(
              "{{learnerName}} received {{dollarAmount}} from {{programName}}",
              {
                learnerName,
                dollarAmount: formatEsaWalletCents(
                  creditPurchase?.amount_cents ?? 0,
                ),
                programName,
              },
            )
          )}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="medium"
          icon={faTimes}
          variant="text"
          color={showPendingBanner ? "info" : "success"}
          sx={{
            ...(showPendingBanner
              ? pendingCloseButtonSx
              : successCloseButtonSx),
            padding: "4px",
          }}
        />
      </Box>
    </Alert>
  );
}
